<script>
  import { onDestroy, onMount } from "svelte";
  import { Input, Label } from "sveltestrap";
  import DataColumns from "./dataColumns.svelte";
  import { dataInfo } from "../../editor/storage.js";
  import { dTypeIcon } from "../../editor/component/data.js";
  import { encodingMap } from "../../editor/component/mark.js";

  import ScaleEditor from "./scaleEditor.svelte";
  import StaticProperty from "./staticProperty.svelte";
  import ItemResponse from "./ItemResponse.svelte";

  export let currentEncoding;
  export let cancelEncodingMenu;
  export let currEncType;
  export let inputFocusAction;
  export let inputBlurAction;
  export let invalidDashPattern;
  export let currLayerPrev;
  export let updateEncoding;
  export let currLayer;
  export let currLayerIndex;
  export let currEncDataType;
  export let currEncSort;
  export let currBin;
  export let currBinCount;
  export let currAggregate;
  export let currScaleScheme;
  export let currEncFieldType;
  export let currEncScaleType;
  export let showDataColumns;

  // dragging
  let dragged;
  function assignToShelf(event, layer) {
    // drop event
    event.preventDefault();
    if (!event.target.classList.contains("shelf")) return false;
    // shelf: row/column/split
    let fieldName = dragged.getAttribute("data-field");
    let encoding = event.target.getAttribute("data-encoding");
    let isCount = dragged.getAttribute("data-agg") === "count";
    let shelf;
    if (event.target.classList.contains("shelf-encoding")) {
      shelf = "encoding";
    }
    // no self assignment
    if (encoding && shelf) {
      currEncDataType = $dataInfo?.stats[fieldName]?.type;
      updateEncoding(fieldName, encoding, "encoding.field", layer);
    } else if (isCount && shelf) {
      currEncDataType = "quantitative";
      updateEncoding("count", encoding, "encoding.aggregate", layer);
    }
  }
  function detachFromShelf(event, layer) {
    // drop event
    event.preventDefault();
    if (event.target.classList.contains("shelf")) return false;
    if (dragged) {
      // let fieldName = dragged.getAttribute("data-field");
      // let shelf = dragged.getAttribute("badge-shelf");
      // let shelfIndex = parseInt(dragged.getAttribute("shelf-index"));
      let encoding = event.target.getAttribute("data-encoding");
      updateEncoding(null, encoding, "encoding.field", layer);
    }
  }
  let toBeDetached;
  function shelfDragEnd(event, layer) {
    dragged.classList.remove("dragged");
    if (toBeDetached) {
      detachFromShelf(event, layer || currLayerPrev);
    }
    dragged = undefined;
    toBeDetached = false;
    document.body.classList.remove("detached");
    dragged?.classList.remove("detached");
    document.querySelector("#detached-tracker").style.display = "none";
  }
  function beingDetached(event) {
    if (
      event.target.classList.contains("shelf") ||
      event.target.classList.contains("field-badge")
    ) {
      toBeDetached = false;
      document.body.classList.remove("detached");
      dragged?.classList.remove("detached");
      document.querySelector("#detached-tracker").style.display = "none";
    } else {
      toBeDetached = true;
      document.body.classList.add("detached");
      dragged?.classList.add("detached");
      document.querySelector("#detached-tracker").style.display = "block";
      document.querySelector("#detached-tracker").style.left =
        event.clientX + 15 + "px";
      document.querySelector("#detached-tracker").style.top =
        event.clientY + 5 + "px";
    }
  }
  function shelfDragStart(event) {
    dragged = event.target;
    dragged.classList.add("dragged");
  }

  // pipelines
  onMount(() => {
    document.addEventListener("dragover", beingDetached, false);
    document.addEventListener("dragend", shelfDragEnd, false);
  });
  onDestroy(() => {
    document.removeEventListener("dragover", beingDetached, false);
    document.removeEventListener("dragend", shelfDragEnd, false);
  });
</script>

<!-- Encoding editor -->
<!-- title -->
<h3 class="with-close-button" style="position: sticky-top;">
  <div>
    <img
      src={`../image/encoding/${encodingMap[currentEncoding].icon}`}
      class="enc-icon"
      alt={encodingMap[currentEncoding].name + " icon"}
    />{encodingMap[currentEncoding].name}
  </div>
  <div
    on:click={() => {
      cancelEncodingMenu();
    }}
  >
    <i class="bi bi-x" />
  </div>
</h3>
<div class="dupo-menu-pane">
  <!-- channel type: static/encoding -->
  <div class="dupo-menu-pane-row">
    <Label>Channel type</Label>
  </div>
  <div class="dupo-menu-pane-form-row form-inline">
    <Input
      type="radio"
      name="enc_type"
      id="enc_type_encoding"
      class="pt-2"
      value="encoding"
      group={currEncType}
      on:change={() => {
        currEncType = "encoding";
        showDataColumns = true;
      }}
      checked={currEncType === "encoding"}
    />
    <Label for="enc_type_encoding" class="pt-2 me-2">Encoding</Label>
    {#if !encodingMap[currentEncoding].non_static}
      <Input
        type="radio"
        name="enc_type"
        id="enc_type_static"
        class="pt-2"
        value="static"
        group={currEncType}
        on:change={() => {
          currEncType = "static";
          showDataColumns = false;
        }}
        checked={currEncType === "static"}
      />
      <Label for="enc_type_static" class="pt-2 me-1">Static</Label>
    {/if}
  </div>
</div>
{#if currEncType === "static"}
  <div class="dupo-menu-pane">
    <div class="dupo-menu-pane-row">
      <Label for="static_value" class="mb-0">Value</Label>
    </div>
    <StaticProperty
      {currentEncoding}
      {inputFocusAction}
      {inputBlurAction}
      {invalidDashPattern}
      {currLayerPrev}
      {updateEncoding}
      {currLayer}
    />
    {#if invalidDashPattern}
      <div class="dupo-menu-pane-row warn-msg">
        <p class="text-sm">Wrong dash pattern!</p>
      </div>
    {/if}
  </div>
{:else if currEncType === "encoding"}
  <div class="dupo-menu-pane">
    <!-- Field -->
    <div class="dupo-menu-pane-row">
      <Label for="static_value" class="mb-0">Field</Label>
    </div>
    <div class="dupo-menu-pane-row shelf-wrap">
      <div
        class="shelf shelf-encoding"
        data-filled={currLayer.mark[currentEncoding]?.field !== undefined
          ? "true"
          : "false"}
        ondragover="return false"
        data-encoding={currentEncoding}
        on:drop={(event) => {
          assignToShelf(event, currLayerPrev);
        }}
      >
        {#if currLayer.mark[currentEncoding]?.field}
          <span
            data-field={currLayer.mark[currentEncoding]?.field}
            class={"field-badge " +
              $dataInfo.stats[currLayer.mark[currentEncoding]?.field]?.type}
            draggable="true"
            badge-shelf="split"
            shelf-index="0"
            ondragover="return false"
            on:drop={assignToShelf}
            on:dragstart={(event) => {
              shelfDragStart(event);
            }}
          >
            <i
              class={"bi bi-" +
                dTypeIcon[
                  $dataInfo.stats[currLayer.mark[currentEncoding]?.field]?.type
                ] +
                " me-1"}
            />
            {currLayer.mark[currentEncoding]?.field}
          </span>
        {/if}
      </div>
    </div>
  </div>
  <div class="pane-divider" />
  <!-- scale -->
  <ScaleEditor
    {currEncType}
    {currEncDataType}
    {currEncSort}
    {currLayer}
    {currBin}
    {currBinCount}
    {currAggregate}
    {currentEncoding}
    {currScaleScheme}
    {inputFocusAction}
    {inputBlurAction}
    {updateEncoding}
    {currEncFieldType}
    {currLayerPrev}
    {currEncScaleType}
  />
{/if}

<!-- lock -->
<div class="pane-divider" />
<div class="dupo-menu-pane pt-2">
  <ItemResponse
    type="lock"
    tooltipMessage="Prevent suggesting responsive alternatives that make changes to the mark type. Element-Lock applies to its source or target design(s)."
    title="Lock this mark property"
    token={"mark-lock"}
    value={currLayer.mark[currentEncoding]?.lock}
    {inputFocusAction}
    {inputBlurAction}
    changeAction={(event) => {
      updateEncoding(event, currentEncoding, "lock", currLayer);
    }}
  />
</div>
<div class="pane-divider" />
<div class="dupo-menu-pane">
  <div class="dupo-menu-pane-button-row">
    <button
      type="button"
      on:click={() => {
        cancelEncodingMenu();
      }}><i class="bi bi-arrow-left me-1" />Go back</button
    >
  </div>
</div>
<!-- Field List -->
{#if $dataInfo.columns && showDataColumns}
  <DataColumns {shelfDragStart} x="530px" y="30px" layer_specific={currLayerIndex} />
{/if}
<div id="detached-tracker">
  <i class="bi bi-trash" />
</div>

<style>
  #detached-tracker {
    display: none;
    position: fixed;
    padding: 3px;
    background: #c20010;
    font-size: 16px;
    color: white;
    z-index: 999;
    text-align: center;
    line-height: 16px;
  }
  .shelf {
    line-height: 100%;
  }
  .shelf .field-badge {
    margin-bottom: 0;
  }
</style>
